import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import { ArrowLeft, Pencil, Trash2, Receipt, ShoppingCart, Landmark, FileText, History, Plus, AlertTriangle, Paperclip } from "lucide-react"
import { Page, Card, Button, StatusBadge, ProcessStepper, Alert, LifecycleTimeline } from "./ui"
import { ErrorState, LinesSkeleton } from "./components/approvals/parts"
import { ApprovalProgress } from "./components/approvals/OrderCards"
import PaymentRequestActionBar, {
  PAYMENT_REQUEST_ACTION_META,
} from "./components/payment/PaymentRequestActionBar"
import LinkPurchaseModal from "./components/payment/LinkPurchaseModal"
import { PaymentRequestBudgetAllocation } from "./components/payment/PaymentBudgetAllocation"
import NonPoDetailCard from "./components/payment/NonPoDetailCard"
import {
  useGetPaymentRequestQuery,
  useSubmitPaymentRequestMutation,
  usePaymentRequestActionMutation,
  useResubmitPaymentRequestMutation,
  useSendPaymentRequestToAccountingMutation,
  useDeletePaymentRequestMutation,
  useLinkPurchaseToPaymentRequestMutation,
  useDeletePaymentRequestDocumentMutation,
} from "shared/redux/endpoints/procurementPayments"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { PAYMENT_REQUEST_STATUS } from "./status"
import { isAutoRejectedOutcome, AUTO_REJECT_MESSAGE } from "./actionResultFeedback"
import {
  formatVND,
  formatDate,
  formatDateTime,
  approvalLimitExceededMessage,
  PAYMENT_TYPE_OPTIONS,
} from "./constants"
import { HScroll } from "procurement/design/components/HScroll"

// Fixed main path only — Rejected is a side branch shown as a banner, not a
// step, and there is no "Unpaid" step: Sent to Accounting IS the resting
// state until the passive SUN cron marks it Paid.
const PR_STEPS = [
  { key: PAYMENT_REQUEST_STATUS.DRAFT, label: "Draft" },
  { key: PAYMENT_REQUEST_STATUS.PENDING, label: "Waiting for approval" },
  { key: PAYMENT_REQUEST_STATUS.APPROVED, label: "Approved" },
  { key: PAYMENT_REQUEST_STATUS.SENT_TO_ACCOUNTING, label: "Sent to Accounting" },
  { key: PAYMENT_REQUEST_STATUS.PAID, label: "Paid" },
]

// InfoRequested is a branch off Pending — the stepper renders it at the same
// position as Pending (still "waiting on the requester", not a new stage).
const stepperKey = (status) => (status === PAYMENT_REQUEST_STATUS.INFO_REQUESTED ? PAYMENT_REQUEST_STATUS.PENDING : status)

const paymentTypeLabel = (v) => PAYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v || "—"

const DOC_TYPE_LABEL = { invoice_xml: "Invoice XML", invoice_pdf: "Invoice PDF", receipt: "Receipt", other: "Other" }
const formatSize = (bytes) => (bytes > 0 ? `${Math.max(1, Math.round(bytes / 1024)).toLocaleString("vi-VN")} KB` : null)

const Def = ({ label, children, full }) => (
  <div className={full ? "pc-def-full" : undefined}>
    <div className="pc-def-label">{label}</div>
    <div className="pc-def-value">{children ?? "—"}</div>
  </div>
)

// F5 — Payment Request Detail / Approve Payment. This is a decision screen
// first, a record second: the amount, supplier and PO context sit above the
// fold in the main column, and every allowed action renders from
// `allowedActions[]` in the sticky side panel — never hardcoded per status.
const PaymentRequestDetailContainer = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: pr, isLoading, isError, refetch } = useGetPaymentRequestQuery(id, { skip: !id })

  const [submitPr] = useSubmitPaymentRequestMutation()
  const [prAction] = usePaymentRequestActionMutation()
  const [resubmitPr] = useResubmitPaymentRequestMutation()
  const [sendToAccounting] = useSendPaymentRequestToAccountingMutation()
  const [deletePr, { isLoading: deleting }] = useDeletePaymentRequestMutation()
  const [linkPurchase, { isLoading: linking }] = useLinkPurchaseToPaymentRequestMutation()
  const [deleteDocument, { isLoading: deletingDocument }] = useDeletePaymentRequestDocumentMutation()

  const handleDeleteDocument = async (documentId) => {
    try {
      await deleteDocument({ id, documentId }).unwrap()
      showToastSuccess("Attachment removed.")
    } catch (e) {
      showToastError(e?.data?.data?.message || e?.data?.message || "Couldn't remove this attachment.")
    }
  }
  const [busy, setBusy] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [showAddPo, setShowAddPo] = useState(false)

  const back = () => navigate("/procurement/payment-requests")

  const handleDelete = async () => {
    try {
      await deletePr(id).unwrap()
      showToastSuccess("Draft deleted.")
      navigate("/procurement/payment-requests")
    } catch (e) {
      showToastError(e?.data?.data?.message || e?.data?.message || "Couldn't delete this draft.")
    } finally {
      setConfirmDelete(false)
    }
  }

  const handleAddPo = async ({ purchaseId, allocatedAmount }) => {
    try {
      await linkPurchase({ id, purchaseId, allocatedAmount }).unwrap()
      showToastSuccess("Purchase linked.")
      setShowAddPo(false)
      refetch()
    } catch (e) {
      showToastError(e?.data?.data?.message || e?.data?.message || "Couldn't link this purchase.")
    }
  }

  // `deadline` only applies to info_request (see PaymentRequestActionBar).
  const runAction = async (action, note, deadline) => {
    if (busy) return false
    setBusy(true)
    try {
      let result
      if (action === "submit") result = await submitPr(id).unwrap()
      else if (action === "resubmit") result = await resubmitPr({ id, note }).unwrap()
      else if (action === "send_to_accounting") result = await sendToAccounting(id).unwrap()
      else result = await prAction({ id, action, note, deadline }).unwrap()

      // A 200 here only means the call went through — if this PR's
      // info-request deadline had already passed (most likely hit via
      // resubmit), the backend auto-rejects instead of applying the action,
      // and a green success toast would misreport that.
      if (isAutoRejectedOutcome(action, result, PAYMENT_REQUEST_STATUS.REJECTED)) {
        showToastError(AUTO_REJECT_MESSAGE)
      } else {
        showToastSuccess("Done. The payment request has been updated.")
      }
      refetch()
      return true
    } catch (e) {
      const status = e?.status
      if (status === 403) {
        const limitMsg = approvalLimitExceededMessage(
          e?.data?.data?.message || e?.data?.message
        )
        showToastError(limitMsg || "You're not allowed to perform that action.")
      } else showToastError(e?.data?.data?.message || e?.data?.message || "Couldn't complete that action. Please try again.")
      return false
    } finally {
      setBusy(false)
    }
  }

  const backAction = (
    <Button variant="secondary" leftIcon={<ArrowLeft aria-hidden />} onClick={back}>
      Back to Payment Requests
    </Button>
  )

  if (isLoading) {
    return (
      <Page breadcrumb={["Procurement", "Payment requests", "Detail"]} title="Payment Request" actions={backAction}>
        <Card>
          <LinesSkeleton lines={5} />
        </Card>
        <Card>
          <LinesSkeleton lines={4} />
        </Card>
      </Page>
    )
  }

  if (isError || !pr) {
    return (
      <Page breadcrumb={["Procurement", "Payment requests", "Detail"]} title="Payment Request" actions={backAction}>
        <Card>
          <ErrorState
            title="Couldn't load this payment request"
            message="The service didn't respond. Try again in a moment."
            onRetry={refetch}
          />
        </Card>
      </Page>
    )
  }

  const actionLog = pr.actionLog || []
  const approvalProgress = pr.approvalProgress || []
  const isRejected = pr.status === PAYMENT_REQUEST_STATUS.REJECTED
  const isDraftEditable = pr.status === PAYMENT_REQUEST_STATUS.DRAFT
  // Info-requested reopens the same edit form as a Draft (PUT-backed) — the
  // form's bottom action switches to Resubmit there instead of Submit.
  const isInfoRequested = pr.status === PAYMENT_REQUEST_STATUS.INFO_REQUESTED
  const isEditable = isDraftEditable || isInfoRequested
  const isDirect = !!pr.direct

  const headerActions = (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      {backAction}
      {isDraftEditable && (
        <Button variant="danger-outline" leftIcon={<Trash2 aria-hidden />} onClick={() => setConfirmDelete(true)}>
          Delete draft
        </Button>
      )}
      {isEditable && (
        <Button
          variant="secondary"
          leftIcon={<Pencil aria-hidden />}
          onClick={() => navigate(`/procurement/payment-requests/${id}/edit`)}
        >
          {isInfoRequested ? "Edit & Resubmit" : "Edit draft"}
        </Button>
      )}
    </div>
  )

  return (
    <Page
      breadcrumb={["Procurement", "Payment requests", pr.paymentCode || "Detail"]}
      title={pr.paymentCode || "Payment Request"}
      subtitle={pr.batchLabel}
      actions={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <StatusBadge status={pr.status} />
          {headerActions}
        </div>
      }
    >
      {isRejected && (
        <Alert tone="danger" title="This payment request was rejected">
          See the action log below for the reason.
        </Alert>
      )}

      {!isRejected && (
        <Card flush>
          <div style={{ padding: "18px 20px" }}>
            <ProcessStepper steps={PR_STEPS} currentKey={stepperKey(pr.status)} />
            {pr.status === PAYMENT_REQUEST_STATUS.INFO_REQUESTED && (
              <p className="pc-hint" style={{ textAlign: "center", marginTop: 10 }}>
                Info requested — resubmit once you have what the approver needs.
              </p>
            )}
          </div>
        </Card>
      )}

      <div className="pc-review">
        <div className="pc-review-main">
          {pr.overPoWarning && (
            <Alert tone="warn" icon={<AlertTriangle aria-hidden />} title="Over the linked PO's remaining balance">
              This payment is {formatVND(pr.overPoAmount)} more than what was left on the linked PO(s) — it went
              through anyway, just flagging it here.
            </Alert>
          )}
          <Card
            icon={<Receipt aria-hidden />}
            title="Payment Information"
            subtitle={pr.batchLabel}
          >
            <div className="pc-defgrid">
              {isDirect ? (
                <Def label="Type">Non-PO payment</Def>
              ) : (
                <>
                  <Def label="Purchase order">{(pr.linkedPurchases || []).map((p) => p.poCode).join(", ")}</Def>
                  <Def label="Supplier">{(pr.linkedPurchases || []).map((p) => p.vendorName).join(", ")}</Def>
                </>
              )}
              <Def label="Payment type">{paymentTypeLabel(pr.paymentType)}</Def>
              <Def label="Payment timeline">{formatDate(pr.paymentTimeline)}</Def>
              <Def label="Amount" full>
                <span className="pc-total-lg">{formatVND(pr.amount)}</span>
              </Def>
            </div>
          </Card>

          {isDirect && <NonPoDetailCard directOrder={pr.directOrder} />}

          {!isDirect && (
            <Card
              icon={<ShoppingCart aria-hidden />}
              title="Linked Purchase Orders"
              subtitle={`${(pr.linkedPurchases || []).length} PO(s)`}
              action={
                isEditable && (
                  <Button variant="secondary" size="sm" leftIcon={<Plus aria-hidden />} onClick={() => setShowAddPo(true)}>
                    Add PO
                  </Button>
                )
              }
            >
              {(pr.linkedPurchases || []).length === 0 ? (
                <p className="pc-muted">No purchase linked yet.</p>
              ) : (
                <HScroll className="pc-table-wrap">
                  <table className="pc-table">
                    <thead>
                      <tr>
                        <th>PO Code</th>
                        <th>Supplier</th>
                        <th className="pc-th-right">Allocated amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pr.linkedPurchases.map((lp) => (
                        <tr key={lp.id}>
                          <td className="pc-td-strong">{lp.poCode}</td>
                          <td>{lp.vendorName || "—"}</td>
                          <td className="pc-td-right">{formatVND(lp.allocatedAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </HScroll>
              )}
            </Card>
          )}

          <PaymentRequestBudgetAllocation pr={pr} />

          <Card icon={<Landmark aria-hidden />} title="Bank transfer">
            <div className="pc-defgrid">
              <Def label="Receiver name">{pr.bankReceiverName}</Def>
              <Def label="Bank / branch">{pr.bankNameBranch}</Def>
              <Def label="Account number" full>{pr.bankAccount}</Def>
            </div>
          </Card>

          <Card icon={<FileText aria-hidden />} title="Invoice">
            <div className="pc-defgrid">
              <Def label="Invoice serial">{pr.invoiceSerial}</Def>
              <Def label="Invoice number">{pr.invoiceNumber}</Def>
              <Def label="Invoice date">{formatDate(pr.invoiceDate)}</Def>
              <Def label="Supplier tax code">{pr.supplierTaxCode}</Def>
              <Def label="Description" full>{pr.description}</Def>
              {pr.note && <Def label="Note" full>{pr.note}</Def>}
            </div>
          </Card>

          <Card icon={<Paperclip aria-hidden />} title="Attachments" subtitle={`${(pr.documents || []).length} file(s)`}>
            {(pr.documents || []).length === 0 ? (
              <p className="pc-muted">No attachments.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {pr.documents.map((d) => (
                  <div key={d.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      {d.fileUrl ? (
                        <a href={d.fileUrl} target="_blank" rel="noreferrer" className="pc-td-strong">
                          {d.fileName}
                        </a>
                      ) : (
                        <span className="pc-td-strong">{d.fileName}</span>
                      )}
                      <div className="pc-muted" style={{ fontSize: 12 }}>
                        {[DOC_TYPE_LABEL[d.docType] || d.docType, formatSize(d.sizeBytes), d.uploadedByName, formatDateTime(d.uploadedAt)]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                    {isEditable && (
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<Trash2 aria-hidden />}
                        disabled={deletingDocument}
                        onClick={() => handleDeleteDocument(d.id)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          <ApprovalProgress
            steps={approvalProgress}
            subtitle={pr.approvalRuleRequired === false ? "No approval required for this amount" : undefined}
            emptyText={
              pr.approvalRuleRequired === false
                ? "This batch was sent straight to accounting — the resolved approval rule doesn't require a review at this amount."
                : "No approval route resolved yet."
            }
          />

          <Card icon={<History aria-hidden />} title="Action log" subtitle={`${actionLog.length} entr${actionLog.length === 1 ? "y" : "ies"}`}>
            {actionLog.length === 0 ? (
              <p className="pc-muted">No actions logged yet.</p>
            ) : (
              <LifecycleTimeline
                items={actionLog.map((a, idx) => ({
                  title: PAYMENT_REQUEST_ACTION_META[a.action]?.label || a.action || "Action",
                  meta: [a.actor, formatDateTime(a.actedAt), a.note ? `“${a.note}”` : null].filter(Boolean).join(" · "),
                  state: idx === actionLog.length - 1 ? "current" : "done",
                }))}
              />
            )}
          </Card>
        </div>

        <div className="pc-review-side">
          <PaymentRequestActionBar allowedActions={pr.allowedActions} paymentRequest={pr} busy={busy} onAct={runAction} />
        </div>
      </div>

      {confirmDelete && (
        <Dialog open onClose={() => !deleting && setConfirmDelete(false)} fullWidth maxWidth="xs">
          <DialogTitle>Delete this draft?</DialogTitle>
          <DialogContent>
            <MDTypography variant="body2" color="text">
              This payment request draft will be permanently deleted. This can&apos;t be undone.
            </MDTypography>
          </DialogContent>
          <DialogActions>
            <MDButton variant="text" color="secondary" onClick={() => setConfirmDelete(false)} disabled={deleting}>
              Cancel
            </MDButton>
            <MDButton variant="gradient" color="error" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete"}
            </MDButton>
          </DialogActions>
        </Dialog>
      )}

      {showAddPo && (
        <LinkPurchaseModal
          paymentRequestId={id}
          excludePurchaseIds={(pr.linkedPurchases || []).map((lp) => lp.id)}
          busy={linking}
          onCancel={() => setShowAddPo(false)}
          onSubmit={handleAddPo}
        />
      )}
    </Page>
  )
}

export default PaymentRequestDetailContainer
